/**
 * View-model + formatting helpers for the Manage Users view.
 *
 * The Forge prototype was mocked against a rich member object (name, avatar
 * initials, active-runs, suspended state). The real daemon only exposes what
 * `AdminUser` / usage / project endpoints return, so this module adapts the
 * real shape into the shape the UI components consume — deriving a display
 * name and initials from the email, and mapping 2FA-verification state onto a
 * "pending vs active" status. No data is invented: every field traces back to
 * a real endpoint value.
 */
import type { AdminUser, UserUsage } from "../../api/admin";

export type MemberStatus = "active" | "pending";

export type MemberVM = {
  id: number;
  email: string;
  /** Derived from the email local-part — the daemon stores no display name. */
  name: string;
  initials: string;
  role: "admin" | "member";
  /** active = finished 2FA setup; pending = account created, 2FA not yet set up. */
  status: MemberStatus;
  /** True for the signed-in admin's own row. */
  you: boolean;
  /** Rolling-30-day spend, cents. Stands in for "this month". */
  spentCents: number;
  /** Rolling-24h spend, cents. */
  todayCents: number;
  /** Rolling-7-day spend, cents. */
  weekCents: number;
  /** Monthly USD cap in cents; null = unlimited. */
  monthlyCapCents: number | null;
  /** Daily USD cap in cents; null = unlimited. */
  dailyCapCents: number | null;
  lastLoginAt: string | null;
  createdAt: string;
  totpVerified: boolean;
};

/** "grace.chen@thunderay.com" -> "Grace Chen"; "patrik@x.com" -> "Patrik". */
export function deriveName(email: string): string {
  const local = (email.split("@")[0] ?? email).trim();
  const words = local.split(/[._+-]+/).filter(Boolean);
  if (words.length === 0) return email;
  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Two-letter monogram from the derived name, falling back to the email. */
export function initialsFor(name: string, email: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  if (words.length === 1 && words[0]!.length >= 2) return words[0]!.slice(0, 2).toUpperCase();
  const local = email.replace(/[^a-zA-Z0-9]/g, "");
  return (local.slice(0, 2) || "??").toUpperCase();
}

export function toMemberVM(
  u: AdminUser,
  usage: UserUsage | undefined,
  myEmail: string | null,
): MemberVM {
  const name = deriveName(u.email);
  return {
    id: u.id,
    email: u.email,
    name,
    initials: initialsFor(name, u.email),
    role: u.role,
    status: u.totp_verified ? "active" : "pending",
    you: myEmail != null && u.email.toLowerCase() === myEmail.toLowerCase(),
    spentCents: usage?.d30.cents ?? 0,
    todayCents: usage?.d1.cents ?? 0,
    weekCents: usage?.d7.cents ?? 0,
    monthlyCapCents: u.monthly_usd_cents,
    dailyCapCents: u.daily_usd_cents,
    lastLoginAt: u.last_login_at,
    createdAt: u.created_at,
    totpVerified: Boolean(u.totp_verified),
  };
}

/** "$8" for whole dollars, "$8.87" otherwise. */
export function usd(cents: number): string {
  const dollars = cents / 100;
  return "$" + (dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2));
}

/** Compact, always whole-dollar: "$120". */
export function usdShort(cents: number): string {
  return "$" + Math.round(cents / 100).toString();
}

/** Parse a daemon timestamp (ISO or "YYYY-MM-DD HH:MM:SS") into epoch ms, or null. */
function parseTs(value: string | null): number | null {
  if (!value) return null;
  // SQLite datetime("now") strings lack a timezone; treat them as UTC so the
  // relative delta is stable regardless of the viewer's locale.
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
    ? value.replace(" ", "T") + "Z"
    : value;
  const t = Date.parse(normalized);
  return Number.isNaN(t) ? null : t;
}

/** "now", "12m ago", "3h ago", "2d ago", or an absolute date for older values. */
export function relativeTime(value: string | null): string {
  const t = parseTs(value);
  if (t == null) return value ?? "—";
  const diffMs = Date.now() - t;
  if (diffMs < 0) return "now";
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** "Joined Mar 2024" from a created-at timestamp. */
export function joinedLabel(value: string | null): string {
  const t = parseTs(value);
  if (t == null) return value ?? "—";
  return "Joined " + new Date(t).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

/** Single-character monogram for a project glyph tile (colour conveys kind). */
export function projectGlyph(name: string): string {
  const ch = name.trim().match(/[a-zA-Z0-9]/)?.[0];
  return (ch ?? "•").toUpperCase();
}

export const KIND_LABEL: Record<string, string> = {
  addon: "add-on",
  tool: "tool",
  meta: "workspace",
};
